import { config } from '@terminal/config';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import {
  deleteNotionDisconnect,
  postNotionConnect,
} from '@terminal/services/alfred-intergations/alfred-integrations-service';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import {
  ConnectionToast,
  TOAST_ID,
} from '@terminal/views/alfred/research/connection-toast/ConnectionToast';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const isValidNotionServiceName = (
  serviceName?: string | null
): serviceName is 'notion' => serviceName === 'notion';

const CODE_VERIFIER_KEY = 'notion_pkce_code_verifier';
const STATE_KEY = 'notion_pkce_state';
const LOGIN_FAILED_MESSAGE = 'Notion login failed';
const CONNECTION_FAILED_MESSAGE = 'Notion connection failed';

interface UseNotionConnectProps {
  refetchServices: () => void;
}

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateCodeVerifier = () => {
  const bytes = new Uint8Array(64);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

const generateState = () =>
  toBase64Url(crypto.getRandomValues(new Uint8Array(32)));

const generateCodeChallenge = async (codeVerifier: string) => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier)
  );
  return toBase64Url(new Uint8Array(digest));
};

export const useNotionConnect = ({
  refetchServices,
}: UseNotionConnectProps) => {
  const { t } = useTranslation(['chat']);
  const { showToast } = useToasts();
  const { params, deleteQueryParam } = useQueryParams<{
    code: string;
    state: string;
  }>();

  const showConnectionToast = ({
    action = 'connect',
    status,
    deleteConversations,
    message,
  }: {
    action?: 'connect' | 'disconnect';
    status: 'success' | 'error';
    deleteConversations?: boolean;
    message?: string;
  }) => {
    showToast({
      id: TOAST_ID,
      hideToast: true,
      position: ToastPosition.TopRight,
      content: (
        <ConnectionToast
          action={action}
          name={t('chat:services.notion.name')}
          status={status}
          removeConversation={deleteConversations}
          message={message}
        />
      ),
    });
  };

  const connectNotion = async () => {
    const codeVerifier = generateCodeVerifier();
    const state = generateState();

    try {
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(STATE_KEY, state);

      const authParams = new URLSearchParams({
        client_id: config.notion.clientId,
        response_type: 'code',
        redirect_uri: config.notion.redirectUri,
        scope: 'default',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        resource: config.notion.issuer,
      });

      window.location.href = `${config.notion.authorizeUrl}?${authParams}`;
    } catch {
      sessionStorage.removeItem(CODE_VERIFIER_KEY);
      sessionStorage.removeItem(STATE_KEY);
      showConnectionToast({ status: 'error', message: LOGIN_FAILED_MESSAGE });
    }
  };

  const disconnectNotion = async (deleteConversations: boolean) => {
    const res = await deleteNotionDisconnect();

    if (res.data) {
      refetchServices();
      showConnectionToast({
        action: 'disconnect',
        status: 'success',
        deleteConversations,
      });
      return true;
    }

    showConnectionToast({ action: 'disconnect', status: 'error' });
    return false;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount to consume the OAuth redirect's code/state query params
  useEffect(() => {
    const { code, state } = params;

    if (!code || !state) {
      return;
    }

    const storedState = sessionStorage.getItem(STATE_KEY);
    const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);

    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(CODE_VERIFIER_KEY);

    // Must go through React Router's own APIs rather than
    // window.history.replaceState — bypassing the router leaves its search
    // params stale, so a later setSearchParams call (e.g. switching tabs)
    // restores code/state and this effect fires again on remount with no
    // verifier left, showing a false failure toast after a successful connect.
    deleteQueryParam(['code', 'state'], { replace: true });

    if (!codeVerifier || state !== storedState) {
      showConnectionToast({
        status: 'error',
        message: CONNECTION_FAILED_MESSAGE,
      });
      return;
    }

    postNotionConnect({
      extra: {
        code,
        codeVerifier,
        redirectUri: config.notion.redirectUri,
      },
    }).then(res => {
      const errorMessage = res.error?.message ?? CONNECTION_FAILED_MESSAGE;

      if (res.data?.connected) {
        refetchServices();
      }

      showConnectionToast({
        status: res.data?.connected ? 'success' : 'error',
        message: res.data?.connected ? '' : errorMessage,
      });
    });
  }, []);

  return { connectNotion, disconnectNotion };
};
